import { useEffect, useRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { Link } from "react-router-dom";
import {
  useYoutubeFeatured,
  videoWatchUrl,
  playlistWatchUrl,
  type YoutubeVideo,
  type YoutubePlaylist,
} from "../../lib/youtube";
import { useReveal } from "../../lib/hooks";
import { YoutubePlayer } from "../youtube/YoutubePlayer";

/**
 * "On YouTube" landing section — most recent 3 videos + 3 playlists from the
 * Veda Union channel, surfaced via the server-cached `/api/youtube/featured`
 * proxy. Auto-updates whenever the channel publishes new content (server
 * refreshes every 30 min).
 *
 * Uses a lite-embed pattern: each card is a thumbnail with a play affordance.
 * On click, the thumbnail is replaced by an iframe — saves the ~1MB+ youtube
 * player bundle until the user actually wants to watch.
 *
 * Falls back to a "Visit our channel" CTA when the server is unconfigured
 * (no YOUTUBE_API_KEY) or the API returned nothing.
 */


export function YouTubeFeature() {
  const { ref } = useReveal<HTMLDivElement>();
  const q = useYoutubeFeatured();

  const isLoading = q.isLoading;
  const data = q.data;
  const videos = data?.videos ?? [];
  const playlists = data?.playlists ?? [];
  const channelUrl = data?.channelUrl ?? "https://youtube.com/@vedaunion";
  const showFallback =
    !isLoading && (data?.unconfigured || (videos.length === 0 && playlists.length === 0));

  return (
    <section
      id="youtube"
      className="py-20 md:py-28 border-y border-[var(--color-rule)] bg-[var(--color-vellum-dim)]"
    >
      <div className="container-edit">
        <div className="section-heading">
          <span className="section-heading-num tabular">07 / On YouTube</span>
          <hr className="rule flex-1 hidden sm:block" />
          <span className="font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] hidden md:inline whitespace-nowrap">
            classes &amp; events
          </span>
        </div>

        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-16 mb-12 md:mb-16">
          <h2 className="col-span-12 lg:col-span-7 font-display text-[clamp(2.25rem,5.5vw,4.25rem)] leading-[1.05] tracking-[-0.02em]">
            From our{" "}
            <a
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-display-italic underline decoration-[var(--color-rule-violet)] decoration-1 underline-offset-[6px] hover:decoration-[var(--color-violet)] transition-colors"
              style={{ color: "var(--color-violet)" }}
            >
              YouTube channel
            </a>
            .
          </h2>
          <p className="col-span-12 lg:col-span-5 lg:col-start-8 self-end text-[1.05rem] leading-[1.7] text-[var(--color-ink-soft)] lg:max-w-[34ch]">
            Recordings of classes, full Vedic recitations, bhajan evenings, and
            footage from yajñas and gatherings. Subscribe for what comes next.
          </p>
        </div>

        {showFallback ? (
          <FallbackCTA channelUrl={channelUrl} />
        ) : (
          <div ref={ref} className="reveal flex flex-col gap-14 md:gap-16">
            <Carousel
              eyebrow="Latest videos"
              count={isLoading ? 6 : videos.length}
              isLoading={isLoading}
            >
              {(isLoading ? [] : videos).map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </Carousel>

            <Carousel
              eyebrow="Recent playlists"
              count={isLoading && playlists.length === 0 ? 4 : playlists.length}
              isLoading={isLoading && playlists.length === 0}
            >
              {(isLoading && playlists.length === 0 ? [] : playlists).map(
                (p) => (
                  <PlaylistCard key={p.id} playlist={p} />
                ),
              )}
            </Carousel>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-start">
              <Link to="/youtube" className="btn-primary">
                <span>Open the library</span>
                <span className="btn-arrow-icon" aria-hidden>
                  →
                </span>
              </Link>
              <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
              >
                <span>Visit on YouTube</span>
                <span className="btn-arrow-icon" aria-hidden>
                  →
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Horizontally scrollable strip with arrow controls and edge fades.
 *
 * The CSS `scroll-snap-type: x mandatory` plus `scroll-snap-align: start` on
 * each card means the row settles on a clean card edge after each scroll —
 * either from the arrow buttons, a trackpad swipe, or a keyboard arrow key.
 *
 * Arrows scroll by ~one viewport-width-of-cards at a time. Edge fade
 * gradients (.yt-strip::before / ::after) hint at off-screen content; the
 * arrows fade out when the strip reaches its limits so they don't read as
 * dead controls.
 */
function Carousel({
  eyebrow,
  count,
  isLoading,
  children,
}: {
  eyebrow: string;
  count: number;
  isLoading: boolean;
  children: React.ReactNode;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    canPrev: false,
    canNext: false,
  });

  // Recompute scroll affordances when content size changes or the user scrolls.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const recompute = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setScrollState({
        canPrev: scrollLeft > 4,
        canNext: scrollLeft + clientWidth < scrollWidth - 4,
      });
    };
    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", recompute);
      ro.disconnect();
    };
  }, [count, isLoading]);

  const scrollBy = (direction: 1 | -1) => {
    const el = stripRef.current;
    if (!el) return;
    // Scroll by ~85% of the visible width — leaves a little overlap so the
    // user retains spatial context across paging.
    const distance = el.clientWidth * 0.85 * direction;
    el.scrollBy({ left: distance, behavior: "smooth" });
  };

  const items =
    isLoading || count === 0
      ? Array.from({ length: count || 4 }).map((_, i) => (
          <CardSkeleton key={`sk-${i}`} />
        ))
      : (children as React.ReactElement[]);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-5 md:mb-6">
        <p className="eyebrow">{eyebrow}</p>
        {!isLoading && count > 0 && (
          <span className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] tabular">
            {count} latest
          </span>
        )}
      </div>
      <div className="yt-strip-shell">
        <button
          type="button"
          className="yt-strip-arrow is-prev"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll left"
          disabled={!scrollState.canPrev}
        >
          <span aria-hidden>←</span>
        </button>
        <div
          ref={stripRef}
          className="yt-strip"
          tabIndex={0}
          role="region"
          aria-label={eyebrow}
          data-can-prev={scrollState.canPrev}
          data-can-next={scrollState.canNext}
        >
          {items}
        </div>
        <button
          type="button"
          className="yt-strip-arrow is-next"
          onClick={() => scrollBy(1)}
          aria-label="Scroll right"
          disabled={!scrollState.canNext}
        >
          <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}

function VideoCard({ video }: { video: YoutubeVideo }) {
  const [active, setActive] = useState(false);
  return (
    <article className="yt-card">
      <div className="yt-card-frame">
        {active ? (
          <YoutubePlayer kind="video" id={video.id} title={video.title} />
        ) : (
          <button
            type="button"
            className="yt-card-poster"
            onClick={() => setActive(true)}
            aria-label={`Play ${video.title}`}
          >
            <img src={video.thumbnailUrl} alt="" loading="lazy" decoding="async" />
            <span className="yt-play" aria-hidden>
              <Icon name="play" size="1.3em" />
            </span>
          </button>
        )}
      </div>
      <div className="yt-card-meta">
        <a
          href={videoWatchUrl(video.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-display text-[1.2rem] sm:text-[1.3rem] leading-[1.2] tracking-[-0.01em] hover:text-[var(--color-violet)] transition-colors"
        >
          {video.title}
        </a>
        {video.publishedAt && (
          <p className="font-grotesk text-[0.65rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
            {formatDate(video.publishedAt)}
          </p>
        )}
      </div>
    </article>
  );
}

function PlaylistCard({ playlist }: { playlist: YoutubePlaylist }) {
  const [active, setActive] = useState(false);
  const count = playlist.itemCount;
  return (
    <article className="yt-card is-playlist">
      <div className="yt-card-frame">
        {active ? (
          <YoutubePlayer kind="playlist" id={playlist.id} title={playlist.title} />
        ) : (
          <>
            {/* Top-left masthead badge — labels the surface as "playlist" with
                a display-italic count. The number reads as serif body type,
                the surrounding label as functional grotesk; the contrast is
                what tells the reader this is a *collection*, not a single
                piece. */}
            <span className="yt-pl-badge" aria-hidden>
              <span>Playlist</span>
              <span className="yt-pl-badge-num">
                {count > 0 ? count : "—"}
              </span>
            </span>
            <span className="yt-pl-meta" aria-hidden>
              Open series →
            </span>
            <button
              type="button"
              className="yt-card-poster"
              onClick={() => setActive(true)}
              aria-label={`Play ${playlist.title} playlist (${count} ${count === 1 ? "video" : "videos"})`}
            >
              <img
                src={playlist.thumbnailUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <span className="yt-stack-mark" aria-hidden />
              <span className="yt-play" aria-hidden>
                <Icon name="play" size="1.3em" />
              </span>
            </button>
          </>
        )}
      </div>
      <div className="yt-card-meta">
        <a
          href={playlistWatchUrl(playlist.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="yt-pl-title font-display text-[1.25rem] sm:text-[1.35rem] leading-[1.2] hover:text-[var(--color-violet)] transition-colors"
        >
          {playlist.title}
        </a>
        <p className="font-grotesk text-[0.65rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
          {count > 0
            ? `${count} ${count === 1 ? "video" : "videos"} · series`
            : "series"}
        </p>
      </div>
    </article>
  );
}

function CardSkeleton() {
  return (
    <article className="yt-card">
      <div className="yt-card-frame">
        <div className="skel-block w-full h-full" aria-hidden />
      </div>
      <div className="yt-card-meta">
        <div className="skel-block" style={{ width: "82%", height: 16, marginBottom: 8 }} />
        <div className="skel-block" style={{ width: "40%", height: 10 }} />
      </div>
    </article>
  );
}

function FallbackCTA({ channelUrl }: { channelUrl: string }) {
  return (
    <div className="veda-empty">
      <p className="font-display-italic text-[1.5rem] sm:text-[1.85rem] text-[var(--color-ink)] mb-4">
        New videos every week.
      </p>
      <p className="text-[1rem] leading-[1.65] text-[var(--color-ink-soft)] max-w-[55ch] mx-auto mb-8">
        Watch full classes, recordings of yajñas, bhajan evenings, and the
        chants of the four Vedas — directly on our YouTube channel.
      </p>
      <a
        href={channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary"
      >
        <span>Open the channel</span>
        <span className="btn-arrow-icon" aria-hidden>
          →
        </span>
      </a>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
